import React, { useRef, useEffect, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReplySnippet from './ReplySnippet';
import MessageMeta from './MessageMeta';
import MessageActions from './MessageActions';
import MessageReactions from '../MessageReactions';
import { extractEmbeds, renderEmbed } from '../../../Utilities/EmbedRenderer';

const MessageItem = ({
    msg: m, // renamed directly in destructuring for cleaner code
    currentUserId,
    onReply,
    onReact,
    isHighlighted,
    onHighlightEnd,
    onJumpToMessage,
}) => {
    const messageRef = useRef(null);

    // 1. Extract embeds
    const embeds = useMemo(() => extractEmbeds(m?.content || ''), [m?.content]);

    // 2. Determine sender
    const isSelf = useMemo(() => {
        if (m?.fromSelf !== undefined) return m.fromSelf;
        const senderId = m?.sender?._id || m?.sender;
        return String(senderId) === String(currentUserId);
    }, [m, currentUserId]);

    const senderName = !isSelf && (m?.sender?.username || m?.sender?.name);
    const originalMessage = m?.replyTo || null;
    const messageId = m?._id || m?.id;

    // 3. Highlight & Scroll Handling
    useEffect(() => {
        if (isHighlighted && messageRef.current) {
            messageRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });

            // Fallback clear if parent didn't handle the timeout
            if (onHighlightEnd) {
                const timer = setTimeout(() => onHighlightEnd(), 2000);
                return () => clearTimeout(timer);
            }
        }
    }, [isHighlighted, onHighlightEnd]);

    if (!m) return null;

    return (
        <li
            id={`message-${messageId}`}
            ref={messageRef}
            className={`chat-message-wrapper ${isSelf ? 'self' : 'friend'} ${isHighlighted ? 'highlight' : ''}`}
        >
            <div className="message-bubble">
                {!isSelf && senderName && (
                    <div className="sender-name">{senderName}</div>
                )}

                {originalMessage && (
                    <div
                        className="reply-container"
                        onClick={(e) => {
                            e.stopPropagation();
                            onJumpToMessage?.(originalMessage._id || originalMessage.id);
                        }}
                    >
                        <ReplySnippet replyTo={originalMessage} />
                    </div>
                )}

                <div className="message-content">
                    <div className="text-body">
                        {m.content}
                        {m.edited && <span className="edited-tag"> (edited)</span>}
                    </div>

                    {embeds.length > 0 && (
                        <div className="message-embeds-container">
                            {embeds.map((embed, idx) => renderEmbed(embed, idx))}
                        </div>
                    )}
                </div>

                {/* Meta Data (Time & Read Receipts) */}
                <MessageMeta
                    fromSelf={isSelf}
                    createdAt={m.createdAt}
                    readBy={m.readBy || []}
                    currentUserId={currentUserId}
                />

                <MessageReactions
                    reactions={m.reactions || []}
                    currentUserId={currentUserId}
                    onReact={(emoji) => onReact(messageId, emoji)}
                    onShowMore={() => console.log(`Show more reactions for ${messageId}`)}
                />

                <MessageActions
                    onReply={() => onReply?.(m)}
                    onReact={(emoji) => onReact(messageId, emoji || '❤️')}
                />
            </div>
        </li>
    );
};

MessageItem.propTypes = {
    msg: PropTypes.object.isRequired,
    currentUserId: PropTypes.string,
    onReply: PropTypes.func,
    onReact: PropTypes.func.isRequired,
    isHighlighted: PropTypes.bool,
    onHighlightEnd: PropTypes.func,
    onJumpToMessage: PropTypes.func,
};

export default memo(MessageItem);